import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getParticipants, deleteParticipant, getParticipantById, checkUserCanManageParticipants } from '../../services/participantService';
import { getDepartments } from '../../services/departmentService';
import { Participant, Department } from '../../types/models';
import { format, parseISO } from 'date-fns';
import { Plus, User, Search, Download, FileSpreadsheet, FileText, File as FilePdf, Trash2, ChevronLeft, ChevronRight, QrCode, ArrowUp, ArrowDown, Link2, Upload } from 'lucide-react';
import { exportToExcel, exportToCSV, exportToPDF } from '../../utils/export';
import { useAuthStore } from '../../store/authStore';

const ITEMS_PER_PAGE = 15;

type SortDirection = 'asc' | 'desc';

interface SortConfig {
  key: string;
  direction: SortDirection;
}

const ParticipantList: React.FC = () => {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGender, setSelectedGender] = useState<string>('All');
  const [selectedYear, setSelectedYear] = useState<string>('All');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('All');
  const [birthYears, setBirthYears] = useState<number[]>([]);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canManageParticipants, setCanManageParticipants] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const { t } = useTranslation('common');
  const { user } = useAuthStore();
  const navigate = useNavigate();

  // Generate an array of the last 10 years for the dropdown
  const yearsArray = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [participantsData, departmentsData, canManage] = await Promise.all([
          getParticipants(),
          getDepartments(),
          checkUserCanManageParticipants()
        ]);
        setParticipants(participantsData);
        setDepartments(departmentsData);
        setCanManageParticipants(canManage);
        
        // Extract unique birth years
        const years = [...new Set(participantsData.map(p => 
          new Date(p.date_of_birth).getFullYear()
        ))].sort((a, b) => b - a);
        setBirthYears(years);
        setError(null);
      } catch (error: any) {
        console.error('Error fetching data:', error);
        setError(error.message || 'Failed to fetch participants');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this participant?')) {
      return;
    }

    try {
      await deleteParticipant(id);
      setParticipants(prev => prev.filter(p => p.student_id !== id));
      setError(null);
    } catch (err: any) {
      console.error('Error deleting participant:', err);
      setError(err.message || 'Failed to delete participant');
    }
  };

  const handleParticipantClick = async (participantId: string) => {
    try {
      const participant = await getParticipantById(participantId);
      if (!participant) {
        setError('Participant not found');
        return;
      }
      navigate(`/participants/${participantId}`);
    } catch (err: any) {
      console.error('Error navigating to participant:', err);
      setError(err.message || 'Failed to load participant details');
    }
  };

  const requestSort = (key: string) => {
    let direction: SortDirection = 'asc';
    
    if (sortConfig && sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return null;
    }
    
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 inline-block ml-1" /> 
      : <ArrowDown className="h-4 w-4 inline-block ml-1" />;
  };

  // Apply filters and sorting
  const getFilteredAndSortedParticipants = () => {
    // First apply filters
    const needle = searchTerm.toLowerCase();
    const filtered = participants.filter(participant => {
      const matchesSearch =
        `${participant.first_name} ${participant.last_name}`.toLowerCase().includes(needle) ||
        (participant.first_name_tamil?.toLowerCase() || '').includes(needle) ||
        (participant.last_name_tamil?.toLowerCase() || '').includes(needle) ||
        participant.house?.house_name.toLowerCase().includes(needle) ||
        participant.department?.name.toLowerCase().includes(needle) ||
        (participant.reference_number || '').toLowerCase().includes(needle) ||
        (participant.family?.family_name?.toLowerCase() || '').includes(needle) ||
        (participant.family?.father_name?.toLowerCase() || '').includes(needle) ||
        (participant.family?.mother_name?.toLowerCase() || '').includes(needle) ||
        (participant.family?.guardian_name?.toLowerCase() || '').includes(needle);
      
      const matchesGender = selectedGender === 'All' || 
        (selectedGender === 'Male' && participant.gender === 'M') ||
        (selectedGender === 'Female' && participant.gender === 'F');
      
      const participantYear = new Date(participant.date_of_birth).getFullYear();
      const matchesYear = selectedYear === 'All' || participantYear === parseInt(selectedYear);

      const matchesDepartment = selectedDepartment === 'All' || participant.department_id === selectedDepartment;
      
      return matchesSearch && matchesGender && matchesYear && matchesDepartment;
    });

    // Then apply sorting if configured
    if (sortConfig !== null) {
      return [...filtered].sort((a, b) => {
        // Handle nested properties with dot notation
        const getNestedProperty = (obj: any, path: string) => {
          return path.split('.').reduce((prev, curr) => {
            return prev ? prev[curr] : null;
          }, obj);
        };
        
        let aValue = getNestedProperty(a, sortConfig.key);
        let bValue = getNestedProperty(b, sortConfig.key);
        
        // Special handling for date_of_birth
        if (sortConfig.key === 'date_of_birth') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        
        // Handle different data types
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        // For numbers, dates, etc.
        return sortConfig.direction === 'asc' 
          ? (aValue > bValue ? 1 : -1)
          : (bValue > aValue ? 1 : -1);
      });
    }

    return filtered;
  };

  const filteredParticipants = getFilteredAndSortedParticipants();

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGender, selectedYear, selectedDepartment, sortConfig]);

  const totalPages = Math.ceil(filteredParticipants.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedParticipants = filteredParticipants.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleExport = (format: 'excel' | 'csv' | 'pdf') => {
    switch (format) {
      case 'excel':
        exportToExcel(filteredParticipants);
        break;
      case 'csv':
        exportToCSV(filteredParticipants);
        break;
      case 'pdf':
        exportToPDF(filteredParticipants);
        break;
    }
    setShowExportMenu(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('participants.title')}</h1>
        <div className="mt-3 md:mt-0 flex flex-wrap gap-3">
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('participants.actions.export')}
            </button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1" role="menu">
                  <button
                    onClick={() => handleExport('excel')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    {t('participants.export.toExcel')}
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    {t('participants.export.toCSV')}
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FilePdf className="h-4 w-4 mr-2" />
                    {t('participants.export.toPDF')}
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <Link
            to="/participants/cards"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <QrCode className="h-4 w-4 mr-2" />
            {t('participants.cards.title')}
          </Link>
          
          <Link
            to="/participants/family-connections"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Link2 className="h-4 w-4 mr-2" />
            {t('participants.actions.familyConnections')}
          </Link>
          
          {user && canManageParticipants && (
            <>
              <Link
                to="/participants/import"
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Upload className="h-4 w-4 mr-2" />
                Import CSV
              </Link>
              <Link
                to="/participants/new"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('participants.actions.addStudent')}
              </Link>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('participants.filters.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('participants.filters.allGenders')}</option>
                <option value="Male">{t('participants.filters.male')}</option>
                <option value="Female">{t('participants.filters.female')}</option>
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('participants.filters.allBirthYears')}</option>
                {birthYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('participants.filters.allDepartments')}</option>
                {departments.map(dept => (
                  <option key={dept.department_id} value={dept.department_id}>{dept.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredParticipants.length === 0 ? (
          <div className="text-center py-12">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('participants.noStudents.title')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedGender !== 'All' || selectedYear !== 'All' || selectedDepartment !== 'All'
                ? t('participants.noStudents.description.withFilters')
                : t('participants.noStudents.description.noFilters')}
            </p>
            {user && canManageParticipants && (
              <div className="mt-6">
                <Link
                  to="/participants/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('participants.actions.addStudent')}
                </Link>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('first_name')}
                    >
                      {t('participants.table.name')}
                      {getSortIcon('first_name')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('first_name_tamil')}
                    >
                      {t('participants.table.tamilName')}
                      {getSortIcon('first_name_tamil')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('date_of_birth')}
                    >
                      {t('participants.table.dateOfBirth')}
                      {getSortIcon('date_of_birth')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('gender')}
                    >
                      {t('participants.table.gender')}
                      {getSortIcon('gender')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('house.house_name')}
                    >
                      {t('participants.table.house')}
                      {getSortIcon('house.house_name')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('department.name')}
                    >
                      {t('participants.table.department')}
                      {getSortIcon('department.name')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('reference_number')}
                    >
                      {t('participants.table.referenceNumber')}
                      {getSortIcon('reference_number')}
                    </th>
                    {user && canManageParticipants && (
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">{t('participants.table.actions')}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedParticipants.map((participant) => (
                    <tr key={participant.student_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleParticipantClick(participant.student_id)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800"
                        >
                          {participant.first_name} {participant.last_name}
                        </button>
                        {(participant.family?.father_name || participant.family?.mother_name || participant.family?.guardian_name) && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {participant.family.father_name && <span className="mr-2">F: {participant.family.father_name}</span>}
                            {participant.family.mother_name && <span className="mr-2">M: {participant.family.mother_name}</span>}
                            {participant.family.guardian_name && <span>G: {participant.family.guardian_name}</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {participant.first_name_tamil} {participant.last_name_tamil}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {format(parseISO(participant.date_of_birth), 'dd.MM.yyyy')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          participant.gender === 'M' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                        }`}>
                          {participant.gender === 'M' ? t('participants.filters.male') : t('participants.filters.female')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div className="flex items-center">
                          <div 
                            className="h-3 w-3 rounded-full mr-2" 
                            style={{ backgroundColor: participant.house?.house_color }}
                          />
                          {participant.house?.house_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {participant.department ? (
                            <div>
                              <div>{participant.department.name}</div>
                              {participant.department.name_tamil && (
                                <div className="text-xs text-gray-500">{participant.department.name_tamil}</div>
                              )}
                            </div>
                          ) : (
                            '-'
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {participant.reference_number || '-'}
                        </div>
                      </td>
                      {user && canManageParticipants && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link to={`/participants/${participant.student_id}`} className="text-blue-600 hover:text-blue-800 mr-4">
                            {t('participants.actions.view')}
                          </Link>
                          <Link to={`/participants/${participant.student_id}/edit`} className="text-indigo-600 hover:text-indigo-800 mr-4">
                            {t('participants.actions.edit')}
                          </Link>
                          <button
                            onClick={() => handleDelete(participant.student_id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-5 w-5 inline-block" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(startIndex + ITEMS_PER_PAGE, filteredParticipants.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredParticipants.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ParticipantList;